// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { CustomAppState } from "@flip/App/Store/Reducers";
import { xIsAuthenticated } from "@flip/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@flip/XDL/constants";
import { color, layoutContainer, xScreen } from "@flip/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import {
    NavigationModeCookieName,
    NavigationModeMenuStyles,
} from "@insite/content-library/Components/NavigationModeMenu";
import { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    currentMode: getCookie(NavigationModeCookieName) || "Storefront",
    // XNG-Changes
    isAuthenticated: xIsAuthenticated(state),
    isTabletScreen: state.xCommon.isTabletScreen,
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

// XNG-Changes: style
export const Navigation = styled.div<{ isEditingInShell: boolean }>`
    color: ${color("text.main")};
    font-size: 12px;
    line-height: 18px;
    background-color: ${color("common.ui1")};
    height: ${({ isEditingInShell }) => (isEditingInShell ? "auto" : "30px")};
    text-transform: uppercase;
    display: flex;
    align-items: center;
    padding-top: 4px;
    padding-bottom: 4px;
    ${layoutContainer}
`;

const NavItem = styled.span<InjectableCss & { isAuthenticated?: boolean }>`
    height: 100%;
    display: inline-flex;
    align-items: center;
    &.ship-to-address-menu {
        flex-grow: 1;
        max-width: 50%;
        justify-content: space-between;
        ${xScreen("lte", "md")} {
            max-width: unset;
            border: none;
        }
    }

    &.header-link-list {
        margin-left: auto;
        max-width: 50%;
        & > div {
            width: 100%;
        }
        & a > p {
            font-size: inherit;
        }
        ${xScreen("lte", "md")} {
            display: none;
        }
    }
    ${injectCss}
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    showInventoryLink?: InjectableCss;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingLink?: InjectableCss;
    showPricingToggle?: ShowPricingToggleStyles;
    currencyLink?: InjectableCss;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
}

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    showInventoryLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    showPricingLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    currencyLink: {
        css: css`
            margin-left: 16px;
        `,
    },
    // languageLink: {
    //     css: css`
    //         flex: 0 0 auto;
    //         margin-left: 16px;
    //     `,
    // },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 30px;
            min-width: 0;
        `,
    },
    // signInLink: {
    //     css: css`
    //         flex: 0 0 auto;
    //         margin-left: 6px;
    //     `,
    // },
};

const defaultCustomCss = `.wrapper {
}

.navigation-mode-menu {
}

.currency-menu {
}

.language-menu {
}

.ship-to-address-menu {
}

.sign-in-link {
}`;

const SecondaryNavigation: FC<Props> = ({
    id,
    enableWarehousePickup,
    displayPricingAndInventory,
    currentMode,
    fields,
    // XNG-Changes
    isAuthenticated,
    isTabletScreen,
    isCheckoutPage,
}) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);
    // <XNG-Changes>
    const isEditingInShell = useShellContext().isEditing;
    // </XNG-Changes>
    const customCssWrapper = {
        css: css`
            border-bottom: 1px solid ${color("common.border")};
            ${fields.customCSS}
        `,
    };

    // XNG-Changes: structure changes
    if ((!isAuthenticated && isTabletScreen) || isCheckoutPage) {
        return null;
    }
    return (
        <StyledWrapper {...customCssWrapper}>
            {/* <Hidden below="lg"> */}
            <Navigation className="wrapper" {...styles.wrapper}>
                {/* <NavItem className="navigation-mode-menu">
                    <NavigationModeMenu extendedStyles={styles.navigationModeMenu} />
                </NavItem>
                {fields.showInventoryToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                    <NavItem className="show-inventory-toggle" {...styles.showInventoryLink}>
                        <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                    </NavItem>
                )}
                {fields.showPricingToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                    <NavItem className="show-pricing-toggle" {...styles.showPricingLink}>
                        <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                    </NavItem>
                )}
                <NavItem className="currency-menu" {...styles.currencyLink}>
                    <Zone zoneName="Currency" contentId={id} fixed />
                </NavItem>
                <NavItem className="language-menu" {...styles.languageLink}>
                    <Zone zoneName="Language" contentId={id} fixed />
                </NavItem> */}
                {(isAuthenticated || isEditingInShell) && (
                    <NavItem className="ship-to-address-menu" {...styles.warehousePickupLink}>
                        <Zone zoneName="ShipToAddress" contentId={id} fixed />
                    </NavItem>
                )}
                <NavItem className="header-link-list">
                    <Zone zoneName="LinkList" contentId={id} />
                </NavItem>
                {/* <NavItem className="sign-in-link" {...styles.signInLink}>
                        <Zone zoneName="SignIn" contentId={id} fixed />
                    </NavItem> */}
            </Navigation>
            {/* </Hidden> */}
        </StyledWrapper>
    );
};

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps)(SecondaryNavigation),
    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            // },
        ],
    },
};

export default secondaryNavigation;
